import { useAppStore, type AppState } from '../store/app'

// System back → one level up inside the app.
//
// The app's navigation lives in the store (navStack + sheets), not in browser
// history, so on its own the browser's history never grows. That broke system
// back: Android's MainActivity does `webView.canGoBack() ? goBack() : finish()`,
// and with nothing in history every back gesture fell through to finish() — the
// back gesture on a chat screen closed the whole app. The browser's back button
// had the same problem.
//
// Fix, web-only: while there is anything to go back from, keep exactly ONE
// "guard" entry pushed on top of the page's history. A system back pops the
// guard (canGoBack() is now true, so Android goes back instead of finishing),
// we catch the popstate, step back one level in the app, and re-push the guard
// if there is still somewhere to go back from. At the root the guard is gone,
// so the next back leaves the app as the platform expects.
//
// One guard rather than one entry per screen keeps this from having to mirror
// every push/pop into history exactly. The stacks change from many places
// (push taps, logout, in-app back buttons), and the only question that matters
// is whether a back is currently possible.

type BackState = Pick<AppState, 'navStack' | 'sheets' | 'singleAskRequestID' | 'pendingRequests'>

// How many levels a back can pop. Sheets sit above pushed screens, so they
// close first.
function depth(s: BackState): number {
  return s.navStack.length + s.sheets.length
}

// A permission request shown in the full-screen overlay is never dismissed by a
// back gesture. Accidentally clearing an approval is the wrong failure; it keeps
// its explicit buttons. Back is swallowed while it's up.
function backBlocked(s: BackState): boolean {
  return !!s.singleAskRequestID && s.pendingRequests.some((r) => r.id === s.singleAskRequestID)
}

// Step back one level: the top sheet if any, otherwise the top pushed screen.
export function goBackOneLevel(): void {
  const s = useAppStore.getState()
  if (s.sheets.length > 0) s.popSheet()
  else if (s.navStack.length > 0) s.popNav()
}

const GUARD_KEY = 'hearthBack'

export function installBackHistory(): () => void {
  if (typeof window === 'undefined' || !window.history?.pushState) return () => {}

  // history.back() calls we make ourselves (to drop a stale guard). Their
  // popstate must not be treated as a user back.
  let ownPops = 0

  const onGuard = () =>
    (window.history.state as Record<string, unknown> | null)?.[GUARD_KEY] === true

  // Make history agree with the store: a guard on top iff a back is possible.
  // Safe to call any time; it reads the current history state rather than a
  // local flag, so a race between our own back() and a fresh push self-heals on
  // the next call.
  const reconcile = () => {
    const d = depth(useAppStore.getState())
    if (d > 0 && !onGuard()) {
      window.history.pushState({ [GUARD_KEY]: true }, '', window.location.href)
    } else if (d === 0 && onGuard()) {
      ownPops++
      window.history.back()
    }
  }

  const onPopState = () => {
    if (ownPops > 0) {
      ownPops--
      reconcile()
      return
    }
    // A real system back. The guard is already gone from history; do the
    // in-app step (unless blocked), then reconcile re-pushes the guard if
    // there is still a level to go back from.
    if (!backBlocked(useAppStore.getState())) goBackOneLevel()
    reconcile()
  }

  window.addEventListener('popstate', onPopState)
  const unsubscribe = useAppStore.subscribe((s, prev) => {
    if (depth(s) !== depth(prev)) reconcile()
  })
  reconcile()

  return () => {
    window.removeEventListener('popstate', onPopState)
    unsubscribe()
  }
}
